import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { map, tap, catchError } from 'rxjs/operators';
import { publicMethods, baseUrl } from '../globals';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  // Loading states for UI spinners
  isLoading = {
    loadingNotifications: false,
    loadingNotificationCount: false,
    loadingMarkAsRead: false
  };

  constructor(
    private http: HttpClient,
    private pubMethods: publicMethods,
    private auth: AuthenticationService
  ) {}

  /**
   * Fetch the count for a specific notification type.
   * Endpoint: GET /user_app/notifications/counts/
   * @param type - The string key for the notification type (e.g., 'magic_insights', 'zint_grow')
   */
  getNotificationCount(type: string): Observable<number> {
    this.isLoading.loadingNotificationCount = true;

    return this.http
      .get<any[]>(
        baseUrl + '/notifications/counts/',
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        // The backend returns an array: [{type: 'magic_insights', count: 5}, ...]
        // Filter the array to find the specific type requested
        map(counts => {
          const match = counts.find(c => c.type === type);
          return match ? match.count : 0;
        }),
        tap(_ => this.isLoading.loadingNotificationCount = false),
        catchError(this.pubMethods.handleError(`getNotificationCount-${type}`, 0, this.isLoading))
      );
  }

  /**
   * Fetch the list of notifications, optionally filtered by type.
   * Endpoint: GET /user_app/notifications/
   * @param type - (Optional) The string key to filter by.
   */
  getNotifications(type?: string): Observable<any[]> {
    this.isLoading.loadingNotifications = true;

    const options = this.auth.helperGetVerifiedGetParams();

    if (type) {
        options.params = new HttpParams().set('type', type);
    }

    return this.http
      .get<any[]>(
        baseUrl + '/notifications/',
        options
      )
      .pipe(
        tap(_ => this.isLoading.loadingNotifications = false),
        catchError(this.pubMethods.handleError('getNotifications', [], this.isLoading))
      );
  }

  /**
   * Mark a single notification as read by ID.
   * Endpoint: POST /user_app/notifications/{id}/mark_read/
   */
  markNotificationAsRead(id: number): Observable<any> {
    this.isLoading.loadingMarkAsRead = true;

    return this.http
      .post(
        `${baseUrl}/notifications/${id}/mark_read/`,
        {},
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => this.isLoading.loadingMarkAsRead = false),
        catchError(this.pubMethods.handleError('markNotificationAsRead', null, this.isLoading))
      );
  }

  /**
   * Mark all notifications (optionally filtered by type) as read.
   * Endpoint: POST /user_app/notifications/mark_all_read/
   */
  markAllNotificationsAsRead(type?: string): Observable<any> {
    this.isLoading.loadingMarkAsRead = true;

    const options = this.auth.helperGetVerifiedPostDict();
    const params = type ? new HttpParams().set('type', type) : new HttpParams();

    return this.http
      .post(
        baseUrl + '/notifications/mark_all_read/',
        {},
        { ...options, params: params }
      )
      .pipe(
        tap(_ => this.isLoading.loadingMarkAsRead = false),
        catchError(this.pubMethods.handleError('markAllNotificationsAsRead', null, this.isLoading))
      );
  }
}
